import React, {
    useState,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useCallback,
} from "react";
import prizeMoney from "./monies.json";
import {
    createInitialRankings,
    computeLeaderboardState,
} from "./computeLeaderboardState";
import { fetchLastHolesForAthleteIds } from "./espnRoundDetails";
import { TabHeader } from "./components/TabHeader";
import { RankingsSection } from "./components/RankingsSection";
import { OddsSection } from "./components/OddsSection";
import { LastThreeSection } from "./components/LastThreeSection";
import { MoreMenuDrawer } from "./components/MoreMenuDrawer";
import { PlayerDetailModal } from "./components/PlayerDetailModal";
import { PastWinnersFab } from "./components/PastWinnersFab";
import { PastWinnersModal } from "./components/PastWinnersModal";
import { PlayerModalContext } from "./PlayerModalContext";

const LEADERBOARD_POLL_MS = 15_000;

function App() {
    const formatter = useMemo(
        () =>
            new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
            }),
        [],
    );

    async function getLeaders() {
        const response = await fetch(
            "https://site.web.api.espn.com/apis/site/v2/sports/golf/leaderboard",
            {
                headers: {
                    accept: "*/*",
                    "accept-language": "en-GB,en-US;q=0.9,en;q=0.8",
                    "sec-fetch-dest": "empty",
                    "sec-fetch-mode": "cors",
                    "sec-fetch-site": "same-site",
                },
                referrer: "https://www.espn.com/",
                referrerPolicy: "strict-origin-when-cross-origin",
                body: null,
                method: "GET",
                mode: "cors",
                credentials: "omit",
            },
        );
        return response.json();
    }

    const [moneyBreakdown, setMoneyBreakdown] = useState({});
    const [pairingGroups, setPairingGroups] = useState([]);
    const [rankings, setRankings] = useState(createInitialRankings);
    const [playerModalName, setPlayerModalName] = useState(null);
    const [playerCatalog, setPlayerCatalog] = useState({});
    const [cutPickNames, setCutPickNames] = useState([]);
    const [eventMeta, setEventMeta] = useState({
        eventId: "",
        competitionPeriod: 1,
    });

    const openPlayer = useCallback((name) => {
        if (typeof name === "string" && name) setPlayerModalName(name);
    }, []);

    const closePlayerModal = useCallback(() => setPlayerModalName(null), []);
    const [activeTab, setActiveTab] = useState("main");
    const tabScrollYRef = useRef({ main: 0, odds: 0, outsiders: 0 });
    const [pastWinnersOpen, setPastWinnersOpen] = useState(false);
    const [pastWinnersFabScrollVisible, setPastWinnersFabScrollVisible] =
        useState(true);
    const pastWinnersScrollLastY = useRef(0);

    const setActiveTabPreserveScroll = useCallback((nextTab) => {
        setActiveTab((prev) => {
            if (prev === nextTab) return prev;
            tabScrollYRef.current[prev] = window.scrollY;
            return nextTab;
        });
    }, []);

    useLayoutEffect(() => {
        const y = tabScrollYRef.current[activeTab] ?? 0;
        window.scrollTo(0, y);
        pastWinnersScrollLastY.current = window.scrollY ?? 0;
        if ((window.scrollY ?? 0) < 20) setPastWinnersFabScrollVisible(true);
    }, [activeTab]);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [moreMenuTab, setMoreMenuTab] = useState("breakdown");
    const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
    const [showInstallBanner, setShowInstallBanner] = useState(false);
    const [showSplash, setShowSplash] = useState(true);

    const handleOpenMoreMenu = useCallback(() => {
        setPastWinnersOpen(false);
        setMoreMenuOpen(true);
    }, []);

    useEffect(() => {
        if (playerModalName) setPastWinnersOpen(false);
    }, [playerModalName]);

    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY ?? 0;
            const last = pastWinnersScrollLastY.current;
            const delta = y - last;
            pastWinnersScrollLastY.current = y;
            if (y < 20) {
                setPastWinnersFabScrollVisible(true);
                return;
            }
            if (delta > 3) setPastWinnersFabScrollVisible(false);
            else if (delta < -3) setPastWinnersFabScrollVisible(true);
        };
        pastWinnersScrollLastY.current = window.scrollY ?? 0;
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function refreshLeaderboard() {
            try {
                const response = await getLeaders();
                if (cancelled) return;
                const eventId = response?.events?.[0]?.id;
                const competitionPeriodRaw = Number(
                    response?.events?.[0]?.competitions?.[0]?.status?.period,
                );
                const roundPeriod =
                    Number.isFinite(competitionPeriodRaw) &&
                    competitionPeriodRaw > 0
                        ? competitionPeriodRaw
                        : 1;
                const competitors =
                    response?.events?.[0]?.competitions?.[0]?.competitors ??
                    [];
                const pickNames = new Set(
                    createInitialRankings().flatMap((u) => u.picks),
                );
                const athleteIds = [
                    ...new Set(
                        competitors
                            .filter((c) =>
                                pickNames.has(c?.athlete?.displayName),
                            )
                            .map((c) => c.athlete.id),
                    ),
                ];
                let holeDetailsByAthleteId = {};
                if (eventId && athleteIds.length > 0) {
                    try {
                        holeDetailsByAthleteId =
                            await fetchLastHolesForAthleteIds(
                                eventId,
                                athleteIds,
                                roundPeriod,
                            );
                    } catch {
                        holeDetailsByAthleteId = {};
                    }
                }
                if (cancelled) return;
                const {
                    rankings: nextRankings,
                    moneyBreakdown: nextBreakdown,
                    playerCatalog: nextCatalog,
                    cutPickNames: nextCutPickNames,
                    eventId: nextEventId,
                    competitionPeriod: nextPeriod,
                    pairingGroups: nextPairingGroups,
                } = computeLeaderboardState(
                    response,
                    prizeMoney,
                    holeDetailsByAthleteId,
                );
                setRankings(nextRankings);
                setMoneyBreakdown(nextBreakdown);
                setPairingGroups(nextPairingGroups ?? []);
                setPlayerCatalog(nextCatalog ?? {});
                setCutPickNames(nextCutPickNames ?? []);
                setEventMeta({
                    eventId: nextEventId ?? "",
                    competitionPeriod: nextPeriod ?? 1,
                });
            } catch {
                /* keep last good data on fetch/parse errors */
            }
        }

        void refreshLeaderboard();
        const intervalId = window.setInterval(() => {
            void refreshLeaderboard();
        }, LEADERBOARD_POLL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        const onBeforeInstallPrompt = (event) => {
            event.preventDefault();
            setDeferredInstallPrompt(event);
            setShowInstallBanner(true);
        };

        const onAppInstalled = () => {
            setDeferredInstallPrompt(null);
            setShowInstallBanner(false);
        };

        window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
        window.addEventListener("appinstalled", onAppInstalled);

        return () => {
            window.removeEventListener(
                "beforeinstallprompt",
                onBeforeInstallPrompt,
            );
            window.removeEventListener("appinstalled", onAppInstalled);
        };
    }, []);

    useEffect(() => {
        const splashTimeout = window.setTimeout(() => {
            setShowSplash(false);
        }, 3000);

        return () => {
            window.clearTimeout(splashTimeout);
        };
    }, []);

    async function handleInstallClick() {
        if (!deferredInstallPrompt) return;
        deferredInstallPrompt.prompt();
        await deferredInstallPrompt.userChoice;
        setDeferredInstallPrompt(null);
        setShowInstallBanner(false);
    }

    if (showSplash) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-masters-forest via-masters-pine/90 to-masters-forest px-6">
                <img
                    src={`${process.env.PUBLIC_URL}/Bustermm_logo_transparent.png`}
                    alt="Bustermm"
                    className="h-auto w-full max-w-sm animate-splash-pulse motion-reduce:animate-none"
                />
            </div>
        );
    }

    return (
        <PlayerModalContext.Provider value={{ openPlayer }}>
            <div className="min-h-screen bg-gradient-to-b from-masters-forest via-masters-pine/90 to-masters-forest pb-12">
                <TabHeader
                    activeTab={activeTab}
                    setActiveTab={setActiveTabPreserveScroll}
                    onOpenMoreMenu={handleOpenMoreMenu}
                />

                <main className="mx-auto w-full max-w-3xl px-4 pt-6">
                    {activeTab === "main" && (
                        <RankingsSection
                            rankings={rankings}
                            formatter={formatter}
                            cutPickNames={cutPickNames}
                        />
                    )}
                    {activeTab === "odds" && (
                        <OddsSection
                            rankings={rankings}
                            formatter={formatter}
                            cutPickNames={cutPickNames}
                        />
                    )}
                    {activeTab === "outsiders" && (
                        <LastThreeSection
                            rankings={rankings}
                            formatter={formatter}
                            cutPickNames={cutPickNames}
                        />
                    )}
                </main>

                {(activeTab === "main" ||
                    activeTab === "odds" ||
                    activeTab === "outsiders") && (
                    <PastWinnersFab
                        onOpen={() => setPastWinnersOpen(true)}
                        liftForBanner={showInstallBanner}
                        scrollVisible={pastWinnersFabScrollVisible}
                    />
                )}

                <PastWinnersModal
                    open={pastWinnersOpen}
                    onClose={() => setPastWinnersOpen(false)}
                />

                <MoreMenuDrawer
                    open={moreMenuOpen}
                    onOpenChange={setMoreMenuOpen}
                    sheetTab={moreMenuTab}
                    onSheetTabChange={setMoreMenuTab}
                    moneyBreakdown={moneyBreakdown}
                    prizeMoney={prizeMoney}
                    formatter={formatter}
                    pairingGroups={pairingGroups}
                    competitionPeriod={eventMeta.competitionPeriod}
                />

                <PlayerDetailModal
                    displayName={playerModalName}
                    onClose={closePlayerModal}
                    rankings={rankings}
                    playerCatalog={playerCatalog}
                    eventId={eventMeta.eventId}
                    competitionPeriod={eventMeta.competitionPeriod}
                />

                {showInstallBanner && (
                    <div className="fixed bottom-4 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-xl border border-white/20 bg-masters-forest/95 p-4 text-sm text-white shadow-lg backdrop-blur">
                        <p className="mb-3">
                            Install Bustermm for faster access from your home
                            screen.
                        </p>
                        <div className="flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowInstallBanner(false)}
                                className="rounded-md px-3 py-1.5 text-white/80 transition hover:text-white"
                            >
                                Not now
                            </button>
                            <button
                                type="button"
                                onClick={handleInstallClick}
                                className="rounded-md bg-masters-gold px-3 py-1.5 font-medium text-masters-forest transition hover:opacity-90"
                            >
                                Install
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </PlayerModalContext.Provider>
    );
}

export default App;
